import type { SessionType } from '~/types'
import type { AuthenticateBuilderSessionPayload, BuilderSessionData } from '~/composables/api/internal/builder-session/useFetchBuilderSession'
import type { AuthenticateBrowserSessionPayload, BrowserSessionData } from '~/composables/api/internal/browser-session/useFetchBrowserSession'

import { BROWSER_SESSION } from '~/types'

/**
 * Returns session management functions for the specified session type.
 * It's best to use this composable in stores to preserve the session state.
 */

export const useApiSessionManagement = <
  SessionData extends BuilderSessionData | BrowserSessionData,
  AuthenticateSessionPayload extends AuthenticateBuilderSessionPayload & AuthenticateBrowserSessionPayload,
>(sessionType: SessionType) => {
  const useMutateSession = sessionType === BROWSER_SESSION ? useMutateBrowserSession : useMutateBuilderSession
  const useQuerySession = sessionType === BROWSER_SESSION ? useQueryBrowserSession : useQueryBuilderSession

  const { authData } = useSessionStorageAuth(sessionType)
  const sessionData = ref<SessionData | null>(null)

  // authenticate
  const {
    state: authenticationRequestData,
    execute: authenticate,
    error: authenticationRequestError,
  } = useMutateSession.authenticate()

  // TODO: why type inference does not work for authenticationRequestData (should work without `as Ref`)
  whenever(authenticationRequestData as Ref, () => {
    authData.value = authenticationRequestData.value
  })

  // session
  const {
    state: currentSessionRequestData,
    execute: fetchCurrentSession,
  } = useQuerySession.getCurrent({ immediate: false })

  whenever(authData, () => {
    if (!authData.value)
      return
    fetchCurrentSession()
  })

  whenever(currentSessionRequestData, () => {
    sessionData.value = currentSessionRequestData.value
  })

  const isAuthenticated = computed(() => !!sessionData.value)

  const redirectToHandler = async (handlerName: 'success_handler' | 'cancel_handler' | 'delete_handler' | 'error_handler') => {
    if (!sessionData.value)
      throw new Error('Session data not found when redirecting to handler.')

    const handlerUrl = sessionData.value.handlers?.[handlerName]?.url
    if (!handlerUrl)
      throw new Error(`${handlerName} endpoint not found.`)

    window.location.href = handlerUrl
  }

  // Redirect to handlers if needed whenever sessionData is updated
  whenever(() => sessionData.value?.status, (status) => {
    switch (status) {
      case 'completed':
        redirectToHandler('success_handler')
        break
      case 'deleted':
        redirectToHandler('delete_handler')
        break
    }
  })

  const checkAuth = async () => {
    if (isAuthenticated.value)
      return true

    await fetchCurrentSession()

    return isAuthenticated.value
  }

  const cancelSession = () => redirectToHandler('cancel_handler')

  const authenticateSession = (payload: AuthenticateSessionPayload) => authenticate(0, {
    payload,
  })

  return {
    authData,
    checkAuth,
    sessionData,
    authenticateSession,
    isAuthenticated,
    authenticationRequestError,
    cancelSession,
    fetchCurrentSession,
    redirectToHandler,
  }
}
